// eslint-disable-next-line no-restricted-imports
import { createContext } from '@chakra-ui/react';
import { Box } from '@chakra-ui/react/box';
import { List, type ListRootProps } from '@chakra-ui/react/list';
import { Text } from '@chakra-ui/react/text';
import { forwardRef, type ReactNode } from 'react';

import { type BitkitIconComponent } from '../../icons';
import { withSubComponents } from '../../utilities/withSubComponents';

// ----- Context -----

type BitkitListVariant = 'explainer' | 'ordered' | 'unordered' | 'unstyled' | 'inline';
type BitkitListSize = 'lg' | 'md' | 'sm';
type BitkitListTextColor = 'body' | 'secondary';

interface BitkitListContext {
  colorVariant: 'neutral' | 'purple';
  size: BitkitListSize;
  textColor: BitkitListTextColor;
  variant: BitkitListVariant;
}

const [BitkitListProvider, useBitkitListContext] = createContext<BitkitListContext>({
  name: 'BitkitListContext',
  hookName: 'useBitkitListContext',
  providerName: '<BitkitList />',
});

// ----- BitkitList -----

interface BaseProps extends Omit<
  ListRootProps,
  | 'colorPalette'
  | 'columnGap'
  | 'gap'
  | 'gridColumnGap'
  | 'gridGap'
  | 'gridRowGap'
  | 'rowGap'
  | 'size'
  | 'spaceX'
  | 'spaceY'
> {
  children: ReactNode;
  colorVariant?: 'neutral' | 'purple';
  textColor?: BitkitListTextColor;
}

// `sm` is only drawn for the inline run; the block variants start at `md`, so the size set is
// narrowed per variant rather than shared.
interface InlineProps extends BaseProps {
  size?: BitkitListSize;
  variant: 'inline';
}

interface BlockProps extends BaseProps {
  size?: 'lg' | 'md';
  variant?: 'explainer' | 'ordered' | 'unordered' | 'unstyled';
}

export type BitkitListProps = BlockProps | InlineProps;

/** Item spacing is fixed by the list recipe; `gap` and the other spacing props are not accepted. */
const BitkitList = forwardRef<HTMLOListElement | HTMLUListElement, BitkitListProps>((props, ref) => {
  const { children, colorVariant = 'neutral', size = 'lg', textColor = 'body', variant = 'unordered', ...rest } = props;

  const rootProps =
    variant === 'ordered'
      ? ({ as: 'ol', counterReset: 'bitkit-list' } as const)
      : variant === 'explainer'
        ? ({ as: 'ol', counterReset: 'bitkit-list' } as const)
        : variant === 'inline'
          ? ({ alignItems: 'center', flexDirection: 'row', flexWrap: 'wrap', gap: 0 } as const)
          : {};

  return (
    <BitkitListProvider value={{ colorVariant, size, textColor, variant }}>
      <List.Root ref={ref} size={size} {...rootProps} {...rest}>
        {children}
      </List.Root>
    </BitkitListProvider>
  );
});

BitkitList.displayName = 'BitkitList';

// ----- BitkitList.Item -----

export interface BitkitListItemProps {
  children: ReactNode;
  helperText?: ReactNode;
  icon?: BitkitIconComponent;
  iconColor?: string;
}

const UNORDERED_ICON_SIZE: Record<BitkitListSize, '16' | '24'> = {
  lg: '24',
  md: '16',
  sm: '16',
};

const BitkitListItem = forwardRef<HTMLLIElement, BitkitListItemProps>((props, ref) => {
  const { children, helperText, icon: Icon, iconColor } = props;
  const { colorVariant, size, textColor, variant } = useBitkitListContext();

  const content = (
    <Box flex="1" minWidth="0">
      {children}
      {helperText && (
        <Text color="text/secondary" textStyle={size === 'lg' ? 'body/md/regular' : 'body/sm/regular'}>
          {helperText}
        </Text>
      )}
    </Box>
  );

  if (variant === 'unstyled') {
    return (
      <List.Item ref={ref} color={textColor === 'secondary' ? 'text/secondary' : 'text/body'}>
        {content}
      </List.Item>
    );
  }

  if (variant === 'inline') {
    return (
      <List.Item
        ref={ref}
        alignItems="center"
        color={textColor === 'secondary' ? 'text/secondary' : 'text/body'}
        _notFirst={{
          _before: {
            content: '"\\2022"',
            color: 'text/tertiary',
            marginInline: '8',
          },
        }}
      >
        {content}
      </List.Item>
    );
  }

  if (variant === 'ordered') {
    return (
      <List.Item ref={ref} counterIncrement="bitkit-list" gap={helperText ? '12' : undefined}>
        {Icon ? (
          <List.Indicator color={iconColor ?? 'icon/secondary'}>
            <Icon size={UNORDERED_ICON_SIZE[size]} />
          </List.Indicator>
        ) : (
          <List.Indicator _before={{ content: 'counter(bitkit-list) "."' }} />
        )}
        {content}
      </List.Item>
    );
  }

  if (variant === 'explainer') {
    // Marker size and spacing follow the list's size, never an item's helperText, so every
    // marker in one list matches. The recipe sizes the indicator per list size.
    return (
      <List.Item ref={ref} counterIncrement="bitkit-list" gap={size === 'lg' ? '12' : '8'}>
        {Icon ? (
          <List.Indicator color={iconColor ?? `color/${colorVariant}/strong`}>
            <Icon size={UNORDERED_ICON_SIZE[size]} />
          </List.Indicator>
        ) : (
          <List.Indicator
            _before={{ content: 'counter(bitkit-list)' }}
            background={`color/${colorVariant}/subtle`}
            borderRadius="50%"
            color={`color/${colorVariant}/strong`}
            overflow="hidden"
            textStyle="comp/badge/sm"
          />
        )}
        {content}
      </List.Item>
    );
  }

  // unordered (default)
  if (Icon) {
    return (
      <List.Item ref={ref} gap={helperText ? '12' : '8'}>
        <List.Indicator color={iconColor ?? 'icon/secondary'}>
          <Icon size={UNORDERED_ICON_SIZE[size]} />
        </List.Indicator>
        {content}
      </List.Item>
    );
  }

  return (
    <List.Item ref={ref} gap={helperText ? '12' : undefined}>
      <List.Indicator aria-hidden>•</List.Indicator>
      {content}
    </List.Item>
  );
});

BitkitListItem.displayName = 'BitkitListItem';

export default withSubComponents(BitkitList, { Item: BitkitListItem });
